import {FC, useEffect} from 'react'
import {dispatch} from 'app/store'
import {policiesActions} from 'features/Policies/policiesSlice'
import {Policy, SliceStatus} from 'interfaces'
import {LoadingSpinner, RenderIf} from 'components/Basic'
import classNames from 'classnames'
import {useActions, usePolicy} from 'hooks'
import isEqual from 'lodash.isequal'
import {ActionContainer} from 'components/Layouts'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {selectPolicies} from 'features/Policies/policiesSelectors'
import {colorCodes, trailingSlashRegEx} from 'definitions/constant'
import {Button} from 'design'
import {CanIAccess, PERMISSIONS} from 'rbac'

export const PoliciesFooter: FC = () => {
  const {
    updatePolicyStatus,
    policyAssignee,
    cloudApp,
    updatedPolicyField,
    formSubmitted,
    savePressed,
  } = useSelector(selectPolicies)
  const {isBasePolicyToBeColored, pathname, selectedPolicyName} = usePolicy()
  const {isSnackbarActionAvailable} = useActions()
  const {t} = useTranslation(['policies', 'common'])

  const pendingState =
    updatePolicyStatus === SliceStatus.pending ||
    cloudApp.status === SliceStatus.pending

  const onCancel = () => {
    // remove trailing slash, if any
    switch (pathname.replace(trailingSlashRegEx, '')) {
      case '/policies/users':
        dispatch(policiesActions.removeAssignedPolicySnapshot())
        break
      case '/policies/cloudapp':
        dispatch(policiesActions.removeCloudAppSnapshot())
        break
    }
  }

  const saveChanges = () => {
    dispatch(policiesActions.formSubmitted(false))
    dispatch(policiesActions.savePressed(false))
    if (pathname.includes('/policies/cloudapp')) {
      dispatch(
        policiesActions.updateCloudApp({
          policyName: selectedPolicyName,
          cac:
            cloudApp.baseCloudApp &&
            isEqual(cloudApp.data.cac, cloudApp.baseCloudApp.cac) &&
            selectedPolicyName !== Policy.BasePolicy
              ? 'base'
              : cloudApp.data.cac,
        }),
      )
    }
  }

  const onSaveChanges = () => {
    dispatch(policiesActions.savePressed(true))
    dispatch(policiesActions.formSubmitted(false))
    if (pathname.includes('/policies/users')) {
      const {delete_users, delete_groups, add_users, add_groups} =
        policyAssignee
      const payload = {
        policyName: selectedPolicyName,
        add_groups,
        add_users,
        delete_groups,
        delete_users,
      }
      dispatch(policiesActions.assignPolicy(payload))
    } else if (pathname.includes('/policies/cloudapp')) {
      if (updatedPolicyField.value1) {
        document.getElementById('cloud-app-submit')?.click()
      } else {
        saveChanges()
      }
    } else if (pathname.includes('/policies/content')) {
      if (updatedPolicyField.value1 || updatedPolicyField.value2) {
        if (updatedPolicyField.value1) {
          document.getElementById('custom-category-submit')?.click()
        }
        if (updatedPolicyField.value2) {
          document.getElementById('submit-executable')?.click()
        }
      } else {
        saveChanges()
      }
    }
  }

  useEffect(() => {
    if (formSubmitted && savePressed) saveChanges()
  }, [formSubmitted, savePressed])

  return (
    <CanIAccess
      reqPermissions={[
        PERMISSIONS.ADD_EXCEPTION.WRITE,
        PERMISSIONS.URL_CUSTOM_CATEGORY.WRITE,
        PERMISSIONS.ADD_EXCEPTIONS_TO_CUSTOM_CATEGORIES.WRITE,
      ]}
    >
      <ActionContainer
        className={classNames(
          'border-t-0.5 border-gray-400 flex justify-end items-center',
          {
            'bg-dope-teal-100': isBasePolicyToBeColored,
            'bg-dope-black': !isBasePolicyToBeColored,
            hidden: !isSnackbarActionAvailable,
          },
        )}
      >
        <p
          className="text-white karla-bold text-sm mr-6"
          dangerouslySetInnerHTML={{
            __html: t('common:SAVE_FOOTER_MESSAGE'),
          }}
        ></p>
        <Button
          className="mr-3"
          size="md"
          borderRadius="rounded"
          colorScheme={isBasePolicyToBeColored ? 'cyan' : 'red'}
          onClick={onCancel}
          data-cy="policy-footer-cancel"
        >
          {t('common:CANCEL')}
        </Button>

        <Button
          size="sm"
          className="mr-6"
          borderRadius="rounded"
          onClick={onSaveChanges}
          disabled={pendingState}
          data-cy="policy-save"
        >
          <RenderIf isTrue={!pendingState}>{t('common:SAVE')}</RenderIf>
          <RenderIf isTrue={pendingState}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.dopeBlack}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </ActionContainer>
    </CanIAccess>
  )
}
